/**
 * Szkic protokołu offline (plan 3.5). Zmiany formularza i zdjęcia zrobione bez sieci zapisujemy
 * w IndexedDB na telefonie, a po powrocie internetu wysyłamy. Bez IndexedDB (np. testy) działa pamięć.
 */

export type UploadTarget = { kind: 'room'; entryId: string } | { kind: 'meter'; meterId: string }

export interface QueuedUpload {
  id: number
  protocolId: string
  flatId: string
  target: UploadTarget
  blob: Blob
  name: string
  createdAt: number
}

export interface LocalDraft<T> {
  protocolId: string
  form: T
  savedAt: number
}

const DB = 'klucz-offline'
const DRAFTS = 'protocol-drafts'
const UPLOADS = 'protocol-uploads'

let dbPromise: Promise<IDBDatabase | null> | null = null

function open(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  dbPromise ??= new Promise((resolve) => {
    const req = indexedDB.open(DB, 1)
    req.onupgradeneeded = () => {
      req.result.createObjectStore(DRAFTS, { keyPath: 'protocolId' })
      req.result.createObjectStore(UPLOADS, { keyPath: 'id', autoIncrement: true }).createIndex('protocolId', 'protocolId')
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(null)
  })
  return dbPromise
}

function run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  return open().then((db) => {
    if (!db) return undefined
    return new Promise<T | undefined>((resolve, reject) => {
      const req = fn(db.transaction(store, mode).objectStore(store))
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  })
}

// Zapas w pamięci, gdy przeglądarka nie daje IndexedDB
const memoryDrafts = new Map<string, LocalDraft<unknown>>()
const memoryUploads: QueuedUpload[] = []
let memoryId = 1

export async function saveDraft<T>(protocolId: string, form: T) {
  const draft: LocalDraft<T> = { protocolId, form, savedAt: Date.now() }
  if (!(await open())) return void memoryDrafts.set(protocolId, draft)
  await run(DRAFTS, 'readwrite', (s) => s.put(draft))
}

export async function loadDraft<T>(protocolId: string): Promise<LocalDraft<T> | undefined> {
  if (!(await open())) return memoryDrafts.get(protocolId) as LocalDraft<T> | undefined
  return run<LocalDraft<T>>(DRAFTS, 'readonly', (s) => s.get(protocolId) as IDBRequest<LocalDraft<T>>)
}

export async function clearDraft(protocolId: string) {
  if (!(await open())) return void memoryDrafts.delete(protocolId)
  await run(DRAFTS, 'readwrite', (s) => s.delete(protocolId))
}

export async function queueUpload(item: Omit<QueuedUpload, 'id' | 'createdAt'>): Promise<QueuedUpload> {
  if (!(await open())) {
    const queued = { ...item, id: memoryId++, createdAt: Date.now() }
    memoryUploads.push(queued)
    return queued
  }
  const id = await run<IDBValidKey>(UPLOADS, 'readwrite', (s) => s.add({ ...item, createdAt: Date.now() }))
  return { ...item, id: id as number, createdAt: Date.now() }
}

export async function pendingUploads(protocolId: string): Promise<QueuedUpload[]> {
  if (!(await open())) return memoryUploads.filter((u) => u.protocolId === protocolId)
  return (await run<QueuedUpload[]>(UPLOADS, 'readonly', (s) => s.index('protocolId').getAll(protocolId) as IDBRequest<QueuedUpload[]>)) ?? []
}

export async function removeUpload(id: number) {
  if (!(await open())) {
    const i = memoryUploads.findIndex((u) => u.id === id)
    if (i >= 0) memoryUploads.splice(i, 1)
    return
  }
  await run(UPLOADS, 'readwrite', (s) => s.delete(id))
}

/** Brak sieci: przeglądarka mówi „offline” albo fetch rzucił TypeError (nie dotarł do serwera). */
export function isNetworkError(e: unknown) {
  return e instanceof TypeError || (typeof navigator !== 'undefined' && navigator.onLine === false)
}

/** Wylogowanie albo usunięcie konta: szkice i zdjęcia z kolejki znikają z telefonu. */
export async function forgetEverything() {
  memoryDrafts.clear()
  memoryUploads.length = 0
  const db = await open()
  if (!db) return
  db.close()
  dbPromise = null
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(DB)
    req.onsuccess = req.onerror = req.onblocked = () => resolve()
  })
}
